import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PeopleIcon from '@mui/icons-material/People';
import SupportIcon from '@mui/icons-material/Support';
import { Link, MenuList, Paper, SvgIcon, Tooltip, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useMedia } from 'react-use';
import { useShallow } from 'zustand/shallow';

import { composites } from '../../../../../common/constants/access';
import { companyPropertiesConstants, overviewConstants, sideMenuConstants } from '../../../../constants/appConstants';
import { sideMenuStrings } from '../../../../constants/strings';
import ContactLiveChatActive from '../../../../hsbc_img/img/ContactLiveChatActive';
import { StyledMenuItem } from '../../../../material-ui-theme/components/styled/StyledMenuItem';
import { getCombinedApprovalsData } from '../../../../queries/approvals/approvalQuery';
import { companyPropertiesQuery } from '../../../../queries/companyQuery';
import { useGetRoleForUserQuery } from '../../../../queries/rolesQuery';
import { selectedTeamIdQuery, teamsQuery, useGetCurrentTeamQuery } from '../../../../queries/teamQuery';
import { useGetVerifyUserAccess, userQuery } from '../../../../queries/userQuery';
import { isCompanyAdmin, isCompanyOrSupportAdmin, isSupportAdmin } from '../../../../selectors/userSelectors';
import { useZustand } from '../../../../zustandStore';
import IconWrapper, { StyledApprovalIconDisplayName } from '../iconWrapper';

import CreateNewRequestButton from './createNewRequestButton';
import { StyledLink } from './styledComponents';

import type { CompanyPropertiesResponse } from '../../../../queries/companyQuery';
import type { User } from '../../../../zustandStore/slices';
import type { FC, HTMLAttributes, PropsWithChildren } from 'react';

type OnOptionClickArgs = {
	route?: string;
	name?: string;
	isOpenOnDesktopView?: boolean;
	isRequestAsideOpen?: boolean;
	isTeamSwitcherAsideOpen?: boolean;
	currentUser: User | undefined;
};

type SidemenuArgs = {
	user: User | undefined;
	userSelectedTeamData: string[] | undefined;
	isCompanySettingsVisible: boolean | undefined;
	isLoadingIsCompanySettingsVisible: boolean;
	teamId: string | undefined;
	isLoadingUserSelectedTeam: boolean;
};

function getSideMenuOptions({
	user,
	isLoadingUserSelectedTeam,
	userSelectedTeamData,
	isCompanySettingsVisible,
	isLoadingIsCompanySettingsVisible,
	teamId,
}: SidemenuArgs) {
	let userRole = '';
	if (!user || !Array.isArray(user?.roles) || isLoadingUserSelectedTeam || isLoadingIsCompanySettingsVisible) {
		return [];
	}

	const userSelectedTeam = userSelectedTeamData?.[0] || '';
	if (user?.roles && user?.roles.length === 1) {
		userRole = user?.roles[0]?.roleId || '';
	} else if (user?.roles.length > 1) {
		userRole = isCompanyAdmin(user?.roles)
			? overviewConstants.COMPANY_ADMIN
			: user?.roles.find(role => role.securityGroupId === userSelectedTeam)?.roleId || '';
	}

	return sideMenuConstants
		.sideMenuOptions({
			isCompanySettingsVisible: Boolean(isCompanySettingsVisible),
			teamId: teamId || '',
		})
		.filter((option: { name: string; route: string; icon: unknown; roles: string[] }) =>
			option.roles.includes(userRole),
		);
}

type ButtonEventProps = {
	isOpenOnDesktopView: boolean;
	isAsideOpen: boolean;
	openSideMenuOnDesktopView: (isOpenOnDesktopView: boolean) => void;
	isMobileWidth: boolean;
	openSideMenuOnMobileView: (isOpenOnMobileView: boolean) => void;
	setIsAsideOpen: (isOpen: boolean) => void;
	setOtherAsideOpen: (isOpen: boolean) => void;
};

function onButtonEvent({
	isOpenOnDesktopView,
	isAsideOpen,
	openSideMenuOnDesktopView,
	isMobileWidth,
	openSideMenuOnMobileView,
	setIsAsideOpen,
	setOtherAsideOpen,
}: ButtonEventProps) {
	const shouldOpen = isOpenOnDesktopView && !isAsideOpen;
	openSideMenuOnDesktopView(!shouldOpen);
	if (isMobileWidth) {
		openSideMenuOnMobileView(true);
	}
	setIsAsideOpen(shouldOpen || !isAsideOpen);
	setOtherAsideOpen(false);
}

const useShowActivity = (
	userRoles: User['roles'] | undefined,
	companyProperties: CompanyPropertiesResponse | undefined,
) => {
	return useMemo(() => {
		if (!Array.isArray(userRoles) || !companyProperties) {
			return false;
		}

		if (globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP) {
			return true;
		}

		if (!isCompanyAdmin(userRoles) || globalThis.config.IS_DEMO) {
			return true;
		}

		const showActivityValue = companyProperties.showactivity;
		return typeof showActivityValue !== 'object' && (showActivityValue === true || showActivityValue === 'true');
	}, [companyProperties, userRoles]);
};

type OptionType = { route?: string; nestedRoute?: string };

type SideMenuPanelProps = HTMLAttributes<HTMLDivElement>;

const SideMenuPanel: FC<PropsWithChildren<SideMenuPanelProps>> = () => {
	const theme = useTheme();

	const {
		isOpenOnDesktopView = true,
		isRequestAsideOpen,
		isTeamSwitcherAsideOpen,
		viewAllApprovals,
		openSideMenuOnDesktopView,
		openSideMenuOnMobileView,
		setIsOpenTeamSwitcherAside,
		setIsRequestAsideOpen,
		setTeam,
		setUiApprovals,
		setUiActivities,
		uiApprovals,
		uiActivities,
	} = useZustand(
		useShallow(state => ({
			isOpenOnDesktopView: state.sideMenu.isOpenOnDesktopView,
			isRequestAsideOpen: state.aside.requestAside.isOpen,
			requestAside: state.aside.requestAside,
			isTeamSwitcherAsideOpen: state.sideMenu.isTeamSwitcherAsideOpen,
			viewAllApprovals: state.activity.viewAllApprovals,
			openSideMenuOnDesktopView: state.openSideMenuOnDesktopView,
			openSideMenuOnMobileView: state.openSideMenuOnMobileView,
			setIsOpenTeamSwitcherAside: state.setIsOpenTeamSwitcherAside,
			setIsRequestAsideOpen: state.setIsRequestAsideOpen,
			setTeam: state.setTeam,
			setUiApprovals: state.setUiApprovals,
			setUiActivities: state.setUiActivities,
			uiApprovals: state.approvals.list,
			uiActivities: state.activity.list,
		})),
	);

	const { data: user } = useQuery(userQuery);

	const { data: teams = [] } = useQuery(teamsQuery({ state: 'active' }));
	const { data: userSelectedTeamData, isLoading: isLoadingUserSelectedTeam } = useQuery(
		selectedTeamIdQuery('selectedTeamID'),
	);
	const { data: currentTeam, isLoading: isGetCurrentTeamLoading } = useGetCurrentTeamQuery(teams, userSelectedTeamData);
	const { data: currentRole } = useGetRoleForUserQuery(user, userSelectedTeamData?.[0] || '');
	const { data: approvalsData } = useQuery(
		getCombinedApprovalsData(user as User, userSelectedTeamData?.[0] || '', false),
	);
	const { data: companyProperties } = useQuery(
		companyPropertiesQuery(user?.companyAccountId, { showactivity: companyPropertiesConstants.showactivity }),
	);

	const showActivity = useShowActivity(user?.roles, companyProperties);

	const { data: activityData } = useQuery(
		getCombinedApprovalsData(
			user as User,
			userSelectedTeamData?.[0] || '',
			isCompanyOrSupportAdmin(user?.roles || []) ? true : viewAllApprovals,
		),
	);
	const navigate = useNavigate();
	const { data: isCompanySettingsVisible, isLoading: isLoadingIsCompanySettingsVisible } = useGetVerifyUserAccess(
		(composites as any).WRITE_COMPANY,
	);
	const ref = useRef(null);
	const isMobileWidth = useMedia('(max-width: 650px)');

	const count = uiApprovals['pending']['count'] || 0;
	const activityCount = uiActivities['pending']['count'] || 0;

	useEffect(() => {
		if (approvalsData?.groupedApprovals) {
			setUiApprovals(approvalsData.groupedApprovals);
		}
	}, [approvalsData, setUiApprovals]);

	useEffect(() => {
		if (activityData?.groupedApprovals) {
			setUiActivities(activityData.groupedApprovals);
		}
	}, [activityData, setUiActivities]);

	useEffect(() => {
		if (!_.isEmpty(currentTeam)) {
			setTeam(currentTeam);
		}
	}, [currentTeam, setTeam]);

	const onOptionClick = useCallback(
		({
			route = '',
			name = '',
			isOpenOnDesktopView = true,
			isRequestAsideOpen = false,
			isTeamSwitcherAsideOpen = false,
			currentUser,
		}: OnOptionClickArgs) => {
			const teamId = userSelectedTeamData?.[0] || '';
			if (route) {
				if (route === '/dashboard') {
					if (isCompanyAdmin(currentUser?.roles || []) || isSupportAdmin(currentUser?.roles || [])) {
						void navigate('/dashboard');
					} else if (currentRole?.id === 'team_admin' || currentRole?.id === 'manager') {
						void navigate(`dashboard/team/${teamId}`);
					}
				} else if (route === '/wallet' && !isCompanyOrSupportAdmin(currentUser?.roles || [])) {
					void navigate('/wallet/my-cards');
				} else if (route.startsWith('/teams') && route.endsWith('/manage-settlements')) {
					void navigate(`/teams/${teamId}/manage-settlements`);
				} else {
					void navigate(route);
				}

				setIsRequestAsideOpen(false);

				if (isMobileWidth) {
					openSideMenuOnMobileView(false);
				}
			} else if (name === sideMenuStrings.SIDE_MENU_MY_REQUESTS_OPTION) {
				onButtonEvent({
					isOpenOnDesktopView,
					isAsideOpen: isRequestAsideOpen,
					openSideMenuOnDesktopView,
					isMobileWidth,
					openSideMenuOnMobileView,
					setIsAsideOpen: setIsRequestAsideOpen,
					setOtherAsideOpen: setIsOpenTeamSwitcherAside,
				});
			} else if (name === sideMenuStrings.SIDE_MENU_TEAM_SWITCHER_OPTION) {
				onButtonEvent({
					isOpenOnDesktopView,
					isAsideOpen: isTeamSwitcherAsideOpen,
					openSideMenuOnDesktopView,
					isMobileWidth,
					openSideMenuOnMobileView,
					setIsAsideOpen: setIsOpenTeamSwitcherAside,
					setOtherAsideOpen: setIsRequestAsideOpen,
				});
			}
		},
		[
			userSelectedTeamData,
			setIsRequestAsideOpen,
			isMobileWidth,
			navigate,
			currentRole?.id,
			openSideMenuOnMobileView,
			openSideMenuOnDesktopView,
			setIsOpenTeamSwitcherAside,
		],
	);

	const isActiveOption = useCallback((route: string, option: OptionType = { nestedRoute: '' }) => {
		const windowPathname = globalThis.location.pathname;
		const routeRegex = new RegExp(`^${route}(/|$)`);
		if (route === '') {
			// special case for empty paths which opens asides
			return windowPathname === option.nestedRoute;
		}
		return windowPathname === route || routeRegex.test(windowPathname) || windowPathname === option.nestedRoute;
	}, []);

	const linkClassNames = useCallback(
		option =>
			classNames({
				'active-option': option?.route ? isActiveOption(option?.route, option) : isActiveOption('', option),
				link__collapsed: !isOpenOnDesktopView,
			}),
		[isActiveOption, isOpenOnDesktopView],
	);
	const sideMenuOptions = useMemo(
		() =>
			getSideMenuOptions({
				user,
				userSelectedTeamData,
				isCompanySettingsVisible,
				isLoadingIsCompanySettingsVisible,
				isLoadingUserSelectedTeam,
				teamId: userSelectedTeamData?.[0],
			}),
		[
			isCompanySettingsVisible,
			isLoadingIsCompanySettingsVisible,
			user,
			userSelectedTeamData,
			isLoadingUserSelectedTeam,
		],
	);

	const renderSideMenuOptions = useCallback(() => {
		if (!user) {
			return;
		}

		const menuItemClasses = option =>
			classNames({
				expanded: isOpenOnDesktopView,
				collapsed: !isOpenOnDesktopView,
				hasApprovalIcon:
					(option.id === 'approvals' && Number(count) > 0) || (option.id === 'activity' && Number(activityCount) > 0),
				isMobileMenu: isMobileWidth,
			});

		const getIconClassNames = option => {
			return (option.id === 'approvals' && Number(count) > 0) || (option.id === 'activity' && Number(activityCount))
				? 'hasApprovalIcon'
				: '';
		};

		const getIconWrapperClassNames = option => {
			return `${option.id === 'approvals' || option.id === 'activity' ? StyledApprovalIconDisplayName : ''} ${
				isOpenOnDesktopView ? 'expanded' : 'collapsed'
			} ${isActiveOption('/support') ? 'active-icon' : ''}`;
		};

		const shouldRenderOption = option => {
			return !(option.id === 'activity' && !showActivity);
		};

		return sideMenuOptions.filter(shouldRenderOption).map(option => (
			<StyledMenuItem
				key={option.id}
				className={option.id === 'requests' && isRequestAsideOpen ? 'active-option' : linkClassNames(option)}
				style={{ padding: 0 }}
				data-testid={option.id}
				onClick={() =>
					onOptionClick({
						route: option.route,
						name: option.name,
						isOpenOnDesktopView,
						isRequestAsideOpen,
						isTeamSwitcherAsideOpen,
						currentUser: user,
					})
				}
			>
				<StyledLink
					className={menuItemClasses(option)}
					data-testid={`sideMenu-${option.id}-btn`}
					ts-variant="sideMenuLink"
					ts-data={`${option.id === 'approvals' || option.id === 'activity' ? String(option.id === 'approvals' ? (count > 49 ? '49+' : count) : activityCount > 49 ? '49+' : activityCount || 0) : null}`}
				>
					<Tooltip title={option.name}>
						<Typography sx={{ lineHeight: 0.1 }} ref={ref} className={getIconClassNames(option)}>
							<IconWrapper
								icon={() => {
									return globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? (
										<SvgIcon
											className={getIconWrapperClassNames(option)}
											sx={{
												'& path:last-child': {
													fill: 'black !important',
												},
												marginTop: '8px',
												marginLeft: '5px',
												fill: 'black',
												filter: 'invert(1)',
											}}
											component={option.icon}
										/>
									) : (
										<IconWrapper icon={option.icon} className={getIconWrapperClassNames(option)} />
									);
								}}
								className={getIconWrapperClassNames(option)}
							/>
						</Typography>
					</Tooltip>
					<Typography
						component="span"
						ts-variant="sideMenuItemSpan"
						sx={{
							display: 'inline',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							maxWidth: '70vw',
						}}
					>
						{option.name}
					</Typography>

					<ArrowForwardIosIcon
						style={{
							opacity: `${option.hasAside && isOpenOnDesktopView ? 1 : 0}`,
							transition: 'opacity 0.3s ease-in-out',
						}}
						ts-variant="teamSwitcherArrowIcon"
					/>
				</StyledLink>
			</StyledMenuItem>
		));
	}, [
		user,
		sideMenuOptions,
		isOpenOnDesktopView,
		count,
		activityCount,
		isMobileWidth,
		isActiveOption,
		showActivity,
		isRequestAsideOpen,
		linkClassNames,
		onOptionClick,
		isTeamSwitcherAsideOpen,
	]);

	return (
		<>
			{globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? (
				<Typography
					component="div"
					style={{
						backgroundColor: 'hsl(205, 20%, 30%)',
						fontWeight: 350,
						fontSize: isOpenOnDesktopView ? theme.typography.tsFontSize.default : 0,
						color: 'white',
						paddingLeft: '18px',
						paddingBottom: '10px',
						whiteSpace: 'nowrap',
					}}
				>
					Business Virtual Cards
				</Typography>
			) : null}
			<Paper
				data-testid="sideMenu-panel"
				ts-variant="sideMenuPanel"
				onClick={event => {
					event.preventDefault();
					if (event.target === event.currentTarget) {
						// only if we click in the sideMenuPanel body,
						// we want to trigger this event; otherwise it gets triggered
						// when we click on menu options also
						openSideMenuOnDesktopView(!isOpenOnDesktopView);
					}
				}}
				style={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-between',
					height: `calc(100vh - ${theme.tsGoUnit.tsUnitTriple}px - ${theme.tsGoUnit.tsUnitTriple}px)`,
					overflow: 'hidden auto',
					gap: theme.tsGoUnit.tsUnitDouble,
					scrollbarWidth: 'thin',
				}}
			>
				<MenuList>
					{renderSideMenuOptions()}
					<CreateNewRequestButton />
				</MenuList>
				<MenuList ts-variant="supportMenu">
					{!isGetCurrentTeamLoading &&
						!isCompanyAdmin(user?.roles || []) &&
						!isCompanySettingsVisible &&
						teams.length > 1 && (
							<StyledMenuItem
								key={'Team Switcher'}
								className={isTeamSwitcherAsideOpen ? 'active-option' : linkClassNames('')}
								style={{ padding: 0, display: 'block' }}
							>
								<Link
									data-testid="sideMenu-teamSwitcher-btn"
									ts-variant="sideMenuLink"
									onClick={() =>
										onOptionClick({
											route: '',
											name: sideMenuStrings.SIDE_MENU_TEAM_SWITCHER_OPTION,
											isTeamSwitcherAsideOpen,
											currentUser: user,
										})
									}
								>
									<Tooltip title={'Team Switcher'}>
										<Typography ref={ref}>
											<IconWrapper
												icon={() =>
													globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? (
														<SvgIcon
															sx={{
																'& path:last-child': {
																	fill: 'black !important',
																},
																marginTop: '8px',
																marginLeft: '5px',
																filter: 'invert(1)',
															}}
															component={ContactLiveChatActive}
														/>
													) : (
														<PeopleIcon />
													)
												}
												className={`${isOpenOnDesktopView ? 'expanded' : 'collapsed'} ${
													isActiveOption('/support') ? 'active-icon' : ''
												}`}
											/>
										</Typography>
									</Tooltip>
									<Typography component="div" ts-variant="multiLine" style={{ marginLeft: '-30px' }}>
										<Tooltip title={currentTeam?.name || ''}>
											<Typography ts-variant="roleNameSwitcher">{currentTeam?.name || ''}</Typography>
										</Tooltip>
										<Typography
											component="div"
											ts-variant="secondary"
											style={{
												...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
													? {
															color: '#ffffff',
														}
													: {}),
											}}
										>
											{currentRole?.label}
										</Typography>
									</Typography>
									<ArrowForwardIosIcon
										style={{
											opacity: Number(isOpenOnDesktopView),
											transition: 'opacity 0.3s ease-in-out',
										}}
										ts-variant="teamSwitcherArrowIcon"
									/>
								</Link>
							</StyledMenuItem>
						)}
					<StyledMenuItem key={'Support'} className={linkClassNames('/support')} style={{ padding: 0 }}>
						<Link
							ts-variant="sideMenuLink"
							onClick={() => {
								void navigate('/support');
							}}
						>
							<Tooltip title={'Support'}>
								<Typography
									ref={ref}
									style={{
										display: 'flex',
									}}
								>
									<IconWrapper
										icon={() =>
											globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? (
												<SvgIcon
													sx={{
														'& path:last-child': {
															fill: 'black !important',
														},
														marginTop: '8px',
														marginLeft: '5px',
														filter: 'invert(1)',
													}}
													component={ContactLiveChatActive}
												/>
											) : (
												<SupportIcon />
											)
										}
										className={`${isOpenOnDesktopView ? 'expanded' : 'collapsed'} ${
											isActiveOption('/support') ? 'active-icon' : ''
										}`}
									/>
								</Typography>
							</Tooltip>
							<Typography
								component="span"
								ts-variant="sideMenuItemSpan"
								sx={{
									display: 'inline',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									maxWidth: '70vw',
								}}
							>
								{sideMenuStrings.SUPPORT}
							</Typography>
						</Link>
					</StyledMenuItem>
				</MenuList>
			</Paper>
		</>
	);
};

export default SideMenuPanel;
